import React, { useContext } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBTypography,
  MDBBtn,
  MDBTextArea,
  MDBAlert,
  MDBCard,
  MDBCardBody,
  MDBBadge
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';
import { useFormData } from '../hooks/useFormData';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { getFieldValidationRules } from '../validation/lectures';
import { DevContext } from '../../../../../App';

export default function DescriptionStep({ onComplete }) {
  const { t } = useTranslation();
  const { formData, handleFieldChange, hasFieldError, getFieldError } = useFormData();
  const { goToNextStep, goToPreviousStep } = useStepNavigation();
  const { getStepValidation, getStepRequiredFields, getFieldValidation, eventConfig } = useValidation();
  const { isDevMode } = useContext(DevContext);

  // Get validation info from schema
  const stepValidation = getStepValidation('description');
  const requiredFields = getStepRequiredFields('description');
  const descriptionFieldValidation = getFieldValidation('content');

  // Get dynamic character limits from schema
  const contentValidationRules = getFieldValidationRules(eventConfig, 'content', formData);
  const MIN_CHARACTERS = contentValidationRules['text.minLength'] || 500;
  const MAX_CHARACTERS = contentValidationRules['text.maxLength'] || 2000;

  // Initialize description array if it doesn't exist or is not properly formatted
  const descriptions = Array.isArray(formData.content) && formData.content.length > 0
    ? formData.content.map(item => ({
        language: item.language,
        text: item.text || ''  // Ensure text is never undefined
      }))
    : [
        { language: 'de', text: '' },
        { language: 'en', text: '' }
      ];

  const handleDescriptionChange = (language, value) => {
    // Ensure we have both language entries
    let updatedDescriptions = [...descriptions];
    
    // Find existing entry or create new one
    const existingIndex = updatedDescriptions.findIndex(d => d.language === language);
    if (existingIndex >= 0) {
      updatedDescriptions[existingIndex] = {
        ...updatedDescriptions[existingIndex],
        text: value
      };
    } else {
      // Add missing language entry
      updatedDescriptions.push({ language, text: value });
    }
    
    handleFieldChange('content', updatedDescriptions);
  };

  const getDescriptionByLanguage = (language) => {
    const description = descriptions.find(d => d.language === language);
    return description?.text || '';
  };

  const getCharacterCount = (language) => {
    const text = getDescriptionByLanguage(language);
    return text.length;
  };

  const getRemainingChars = (language) => {
    const count = getCharacterCount(language);
    return Math.max(0, MIN_CHARACTERS - count);
  };

  const getExcessChars = (language) => {
    const count = getCharacterCount(language);
    return Math.max(0, count - MAX_CHARACTERS);
  };

  const isLanguageDescriptionValid = (language) => {
    const count = getCharacterCount(language);
    return count >= MIN_CHARACTERS && count <= MAX_CHARACTERS;
  };

  const handleContinue = () => {
    const isValid = descriptions.every(description => 
      description.text.length >= MIN_CHARACTERS && description.text.length <= MAX_CHARACTERS
    ) && stepValidation.isValid;
    
    if (isValid) {
      onComplete?.();
      goToNextStep();
    }
  };

  const isValid = descriptions.every(description => 
    description.text.length >= MIN_CHARACTERS && description.text.length <= MAX_CHARACTERS
  ) && stepValidation.isValid;

  return (
    <div className="d-flex flex-column h-100">
      <MDBContainer className="flex-grow-1 pb-5">
      <MDBTypography tag="h3" className="mb-4">
        {t('Lecture Description')}
      </MDBTypography>
      
      <MDBRow className="mb-4">
        <MDBCol>
          <MDBTypography tag="p" className="mb-4 text-muted">
            {t('Please provide a detailed description of your lecture content in both German and English. Each description should be between {{min}} and {{max}} characters long to give participants a comprehensive understanding of what to expect.', { min: MIN_CHARACTERS, max: MAX_CHARACTERS })}
          </MDBTypography>
          
          {/* German Description */}
          <div className="mb-4">
            <div className="d-flex align-items-center mb-2">
              <ReactCountryFlag 
                countryCode="DE" 
                svg 
                style={{ width: '1.5em', height: '1.5em', marginRight: '0.5rem' }}
              />
              <MDBTypography tag="h6" className="mb-0">
                {t('German Description')}
              </MDBTypography>
            </div>
            
            <MDBTextArea
              value={getDescriptionByLanguage('de')}
              onChange={(e) => handleDescriptionChange('de', e.target.value)}
              label={t('German Description')}
              rows={6}
              counter
              maxLength={MAX_CHARACTERS}
            />
            
            <div className="form-text d-flex justify-content-between">
              <span>
                {getRemainingChars('de') > 0 && (
                  <span className="text-warning">
                    {t('{{count}} more characters needed', { count: getRemainingChars('de') })}
                  </span>
                )}
                {getExcessChars('de') > 0 && (
                  <span className="text-danger">
                    {t('{{count}} characters too many', { count: getExcessChars('de') })}
                  </span>
                )}
                {isLanguageDescriptionValid('de') && (
                  <span className="text-success">
                    ✓
                  </span>
                )}
              </span>
              <span className="text-muted">
                {getCharacterCount('de')}/{MAX_CHARACTERS}
              </span>
            </div>
          </div>
          
          {/* English Description */}
          <div className="mb-4">
            <div className="d-flex align-items-center mb-2">
              <ReactCountryFlag 
                countryCode="US" 
                svg 
                style={{ width: '1.5em', height: '1.5em', marginRight: '0.5rem' }}
              />
              <MDBTypography tag="h6" className="mb-0">
                {t('English Description')}
              </MDBTypography>
            </div>
            
            <MDBTextArea
              value={getDescriptionByLanguage('en')}
              onChange={(e) => handleDescriptionChange('en', e.target.value)}
              label={t('English Description')}
              rows={6}
              counter
              maxLength={MAX_CHARACTERS}
            />
            
            <div className="form-text d-flex justify-content-between">
              <span>
                {getRemainingChars('en') > 0 && (
                  <span className="text-warning">
                    {t('{{count}} more characters needed', { count: getRemainingChars('en') })}
                  </span>
                )}
                {getExcessChars('en') > 0 && (
                  <span className="text-danger">
                    {t('{{count}} characters too many', { count: getExcessChars('en') })}
                  </span>
                )}
                {isLanguageDescriptionValid('en') && (
                  <span className="text-success">
                    ✓
                  </span>
                )}
              </span>
              <span className="text-muted">
                {getCharacterCount('en')}/{MAX_CHARACTERS}
              </span>
            </div>
          </div>
          
          <MDBTypography tag="small" className="text-muted mt-2 d-block">
            {t('Tips for a good description:')}
          </MDBTypography>
          <ul className="small text-muted">
            <li>{t('What topics will be covered?')}</li>
            <li>{t('What will participants learn?')}</li>
            <li>{t('Who is the target audience?')}</li>
            <li>{t('Any prerequisites or background knowledge needed?')}</li>
          </ul>
        </MDBCol>
      </MDBRow>

      {hasFieldError('content') && (
        <MDBRow className="mb-3">
          <MDBCol>
            <MDBAlert color="danger">
              {getFieldError('content')}
            </MDBAlert>
          </MDBCol>
        </MDBRow>
      )}

      {/* Validation Debug Info */}
      {isDevMode && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBCard className="border-info">
              <MDBCardBody>
                <MDBTypography tag="h6" className="mb-3">
                  🔍 Validation Debug Info (Dev Mode) - Description Step
                </MDBTypography>
              
              <div className="mb-2">
                <strong>Step Required Fields:</strong> {requiredFields.join(', ') || 'none'}
              </div>
              
              <div className="mb-2">
                <strong>Character Limits (from Schema):</strong> {MIN_CHARACTERS}-{MAX_CHARACTERS} characters
              </div>
              
              <div className="mb-2">
                <strong>Current Descriptions:</strong>
                <ul>
                  <li>DE: "{getDescriptionByLanguage('de')}" ({getCharacterCount('de')} characters)</li>
                  <li>EN: "{getDescriptionByLanguage('en')}" ({getCharacterCount('en')} characters)</li>
                </ul>
              </div>
              
              <div className="mb-2">
                <strong>Local Validation:</strong>{' '}
                <MDBBadge color={isValid ? 'success' : 'warning'}>
                  DE: {isLanguageDescriptionValid('de') ? '✓' : '✗'} | EN: {isLanguageDescriptionValid('en') ? '✓' : '✗'}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Schema Validation:</strong>{' '}
                <MDBBadge color={stepValidation.isValid ? 'success' : 'danger'}>
                  {stepValidation.isValid ? 'Valid' : 'Invalid'}
                </MDBBadge>
              </div>
              
              {stepValidation.errors.length > 0 && (
                <div className="mb-2">
                  <strong>Schema Errors:</strong>
                  <ul className="mb-0">
                    {stepValidation.errors.map((error, index) => (
                      <li key={index}>
                        <code>{error.field}</code>: {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {descriptionFieldValidation && (
                <div className="mb-2">
                  <strong>Field-Specific Error:</strong>{' '}
                  <code>{descriptionFieldValidation.field}</code>: {descriptionFieldValidation.message}
                </div>
              )}
              
              <div className="mb-2">
                <strong>Continue Button:</strong>{' '}
                <MDBBadge color={isValid ? 'success' : 'warning'}>
                  {isValid ? 'Enabled' : 'Disabled'}
                </MDBBadge>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
      )}

      </MDBContainer>
      
      {/* Fixed button container at bottom */}
      <div className="bg-white" style={{ 
        position: 'sticky', 
        bottom: 0,
        padding: '1rem',
        marginTop: 'auto',
        zIndex: 10
      }}>
        <MDBContainer>
          <MDBRow>
            <MDBCol className="d-flex justify-content-center gap-5">
              <MDBBtn 
                color="secondary" 
                onClick={goToPreviousStep}
                outline
              >
                {t('Back')}
              </MDBBtn>
              <MDBBtn 
                onClick={handleContinue}
                disabled={!isValid}
              >
                {t('Continue')}
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBContainer>
      </div>
    </div>
  );
}